/**
 * Patient access link validity — facility-scoped hours config for report link expiry.
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiDelete, apiGet, apiPost, apiPut, ApiError } from '../utils/apiClient';

export interface PatientAccessLinkValidity {
  id: number;
  facilityId: number;
  hours: number;
  createdAt?: string;
  updatedAt?: string;
}

interface ApiListResponse {
  success?: boolean;
  message?: string;
  data?: PatientAccessLinkValidity[];
}

interface ApiSingleResponse {
  success?: boolean;
  message?: string;
  data?: PatientAccessLinkValidity;
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.response && typeof error.response === 'object') {
      const response = error.response as { message?: string; error?: string };
      if (response.message) {
        return response.message;
      }
      if (response.error) {
        return response.error;
      }
    }
    return error.message;
  }
  return error instanceof Error ? error.message : 'Patient access link validity request failed';
}

class PatientAccessLinkValidityService {
  async list(facilityId: number): Promise<{
    success: boolean;
    data?: PatientAccessLinkValidity[];
    error?: string;
  }> {
    try {
      const response = await apiGet<ApiListResponse>(
        API_ENDPOINTS.PATIENT_ACCESS_LINK_VALIDITY.LIST(facilityId)
      );
      const body = response.data;
      if (!body?.success) {
        return {
          success: false,
          error: body?.message || 'Failed to load patient access link validity',
        };
      }
      return { success: true, data: Array.isArray(body.data) ? body.data : [] };
    } catch (error) {
      return { success: false, error: extractErrorMessage(error) };
    }
  }

  async create(payload: {
    facilityId: number;
    hours: number;
  }): Promise<{ success: boolean; data?: PatientAccessLinkValidity; error?: string }> {
    try {
      const response = await apiPost<ApiSingleResponse>(
        API_ENDPOINTS.PATIENT_ACCESS_LINK_VALIDITY.CREATE,
        {
          facilityId: payload.facilityId,
          hours: payload.hours,
        }
      );
      const body = response.data;
      if (!body?.success || !body.data) {
        return {
          success: false,
          error: body?.message || 'Failed to create patient access link validity',
        };
      }
      return { success: true, data: body.data };
    } catch (error) {
      return { success: false, error: extractErrorMessage(error) };
    }
  }

  async update(
    id: number,
    payload: { facilityId: number; hours: number }
  ): Promise<{ success: boolean; data?: PatientAccessLinkValidity; error?: string }> {
    try {
      const response = await apiPut<ApiSingleResponse>(
        API_ENDPOINTS.PATIENT_ACCESS_LINK_VALIDITY.UPDATE(id),
        {
          facilityId: payload.facilityId,
          hours: payload.hours,
        }
      );
      const body = response.data;
      if (!body?.success || !body.data) {
        return {
          success: false,
          error: body?.message || 'Failed to update patient access link validity',
        };
      }
      return { success: true, data: body.data };
    } catch (error) {
      return { success: false, error: extractErrorMessage(error) };
    }
  }

  async remove(id: number, facilityId: number): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await apiDelete<{ success?: boolean; message?: string }>(
        API_ENDPOINTS.PATIENT_ACCESS_LINK_VALIDITY.DELETE(id, facilityId)
      );
      const body = response.data;
      if (!body?.success) {
        return {
          success: false,
          error: body?.message || 'Failed to delete patient access link validity',
        };
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: extractErrorMessage(error) };
    }
  }
}

export const patientAccessLinkValidityService = new PatientAccessLinkValidityService();
export default patientAccessLinkValidityService;
